import type {
  AppSettingDefinitionInfo,
  AppSettingSetDefinition,
} from "@/backend/apps.types"

export interface LayoutSetting {
  key: string
  definition: AppSettingDefinitionInfo
}

export interface LayoutSet {
  key: string | null
  label?: Record<string, string>
  description?: Record<string, string>
  settings: LayoutSetting[]
}

const NO_ORDER = Number.MAX_SAFE_INTEGER

function normalizeSet(
  editorSet: string | AppSettingSetDefinition | undefined
): AppSettingSetDefinition | null {
  if (editorSet == null) return null
  if (typeof editorSet === "string") return { key: editorSet }
  return editorSet
}

function settingOrder(definition: AppSettingDefinitionInfo): number {
  const order = definition.editor?.order
  return typeof order === "number" ? order : NO_ORDER
}

/**
 * Arranges setting definitions into ordered sets for rendering.
 *
 * Grouping and ordering are read from the opaque `editor` hints (`set`,
 * `order`), so no backend or manifest schema is involved. A set's full
 * metadata (label, description, order) only needs to be declared by one of its
 * members; other members reference it by key. Ungrouped settings render first
 * in a set whose `key` is null.
 */
export function layoutSettings(
  definitions: Record<string, AppSettingDefinitionInfo>
): LayoutSet[] {
  const entries = Object.entries(definitions)
  const manifestIndex = new Map(entries.map(([key], index) => [key, index]))

  const sets = new Map<string | null, LayoutSet>()
  const setOrder = new Map<string | null, number>()

  const ungrouped: LayoutSet = { key: null, settings: [] }
  sets.set(null, ungrouped)

  for (const [key, definition] of entries) {
    const set = normalizeSet(definition.editor?.set)
    const setKey = set?.key ?? null

    let target = sets.get(setKey)
    if (!target) {
      target = { key: setKey, settings: [] }
      sets.set(setKey, target)
    }

    if (set) {
      if (set.label) target.label = set.label
      if (set.description) target.description = set.description
      if (typeof set.order === "number") {
        const existing = setOrder.get(setKey)
        if (existing === undefined || set.order < existing) {
          setOrder.set(setKey, set.order)
        }
      }
    }

    target.settings.push({ key, definition })
  }

  const compareSettings = (a: LayoutSetting, b: LayoutSetting) => {
    const orderDelta = settingOrder(a.definition) - settingOrder(b.definition)
    if (orderDelta !== 0) return orderDelta
    return (manifestIndex.get(a.key) ?? 0) - (manifestIndex.get(b.key) ?? 0)
  }

  for (const set of sets.values()) {
    set.settings.sort(compareSettings)
  }

  const effectiveSetOrder = (set: LayoutSet): number => {
    if (set.key === null) return -1
    const explicit = setOrder.get(set.key)
    if (explicit !== undefined) return explicit
    return set.settings.reduce(
      (min, s) => Math.min(min, settingOrder(s.definition)),
      NO_ORDER
    )
  }

  return [...sets.values()]
    .filter((set) => set.settings.length > 0)
    .sort((a, b) => {
      const delta = effectiveSetOrder(a) - effectiveSetOrder(b)
      if (delta !== 0) return delta
      return (a.key ?? "").localeCompare(b.key ?? "")
    })
}
